import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/apiConfig'; // Adjust the import path as needed

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // Use a ref to track if initial auth check has completed
  const initialAuthCheckComplete = useRef(false);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Skip if we've already done the initial check
      if (initialAuthCheckComplete.current) return;
      
      setIsLoading(true);
      setAuthError(null);
      
      try {
        const token = localStorage.getItem('authToken');
        console.log('Initial auth check with token:', token ? 'Token exists' : 'No token');
        
        if (!token) {
          console.log('No token found, user is not authenticated');
          setIsAuthenticated(false);
          setCurrentUser(null);
        } else {
          // We have a token, fetch user data from backend
          console.log('Token found, fetching user data...');
          try {
            const response = await axios.get(API_ENDPOINTS.user, {
              headers: {
                'Authorization': `Token ${token}`
              }
            });
            
            console.log('User data fetched successfully:', response.data);
            setCurrentUser(response.data);
            setIsAuthenticated(true);
          } catch (error) {
            console.error('Failed to fetch user data:', error);
            // Token might be invalid, remove it
            localStorage.removeItem('authToken');
            setIsAuthenticated(false);
            setCurrentUser(null);
            setAuthError('Session expired. Please login again.');
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthError(error.message);
        setIsAuthenticated(false);
        setCurrentUser(null);
      } finally {
        setIsLoading(false);
        initialAuthCheckComplete.current = true;
      }
    };
    
    checkAuthStatus();
  }, []);

  // This function will be called from your login page
  const login = async (userData) => {
    console.log('Setting auth state with user data:', userData);
    
    try {
      // If userData contains a token, we'll use that
      if (userData?.token) {
        localStorage.setItem('authToken', userData.token);
      }
      
      // Set the current user from the userData
      if (userData?.user) {
        setCurrentUser(userData.user);
      }
      
      // Set authenticated state
      setIsAuthenticated(true);
      setIsLoading(false);
      return true;
    } catch (error) {
      console.error('Login state update failed:', error);
      setAuthError(error.message);
      return false;
    }
  };

  const logout = () => {
    console.log('Logging out user');
    localStorage.removeItem('authToken');
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  // Function to refresh user data
  const refreshUserData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No auth token found');
      }
      
      const response = await axios.get(API_ENDPOINTS.user, {
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      
      setCurrentUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  // For debugging
  useEffect(() => {
    console.log('Auth state changed:', { 
      isAuthenticated, 
      isLoading,
      currentUser: currentUser ? 'User exists' : 'No user',
      hasToken: !!localStorage.getItem('authToken')
    });
  }, [isAuthenticated, isLoading, currentUser]);

  const value = {
    currentUser,
    isAuthenticated,
    isLoading,
    authError,
    login,
    logout,
    setCurrentUser,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);